import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Link } from 'react-router-dom';
import type { DoctorWithServices, DraggedDoctorItem } from '@/shared/types';
import { DRAG_TYPES } from '@/shared/types';

/**
 * Пропсы для компонента DraggableDoctorRow
 */
interface DraggableDoctorRowProps {
  doctor: DoctorWithServices;
  index: number;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onDragEnd: () => Promise<void>;
  onStatusChange: (doctor: DoctorWithServices) => Promise<void>;
}

/**
 * Компонент перетаскиваемой строки врача
 * Реализует drag-and-drop функциональность для изменения порядка врачей
 * @param doctor - Данные врача
 * @param index - Индекс врача в списке
 * @param onMove - Функция перемещения врача
 * @param onStatusChange - Функция изменения статуса врача
 * @returns JSX элемент
 */
export const DraggableDoctorRow: React.FC<DraggableDoctorRowProps> = ({
  doctor,
  index,
  onMove,
  onDragEnd,
  onStatusChange
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  /**
   * Получение URL изображения врача
   */
  const getDoctorImageUrl = (doctor: DoctorWithServices): string | null => {
    if (doctor.assignedImage) {
      // Используем imageUrl напрямую из Supabase Storage
      if (doctor.assignedImage.imageUrl) {
        return doctor.assignedImage.imageUrl;
      }

      // Fallback для старых изображений с relativePath
      if (doctor.assignedImage.relativePath) {
        return `${import.meta.env.VITE_WEB_HOST}${doctor.assignedImage.relativePath}`;
      }
    }
    return null;
  };

  /**
   * Настройка drag функциональности
   */
  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPES.DOCTOR_ROW,
    item: (): DraggedDoctorItem => ({
      type: DRAG_TYPES.DOCTOR_ROW,
      id: doctor.id,
      index,
      sortOrder: doctor.sortOrder
    }),
    end: (_item, monitor) => {
      // Вызываем обновление порядка сортировки при завершении перетаскивания
      if (monitor.didDrop()) {
        onDragEnd();
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  /**
   * Настройка drop функциональности
   */
  const [, drop] = useDrop({
    accept: DRAG_TYPES.DOCTOR_ROW,
    hover: (item: DraggedDoctorItem, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Не делать ничего, если элемент перетаскивается на самого себя
      if (dragIndex === hoverIndex) {
        return;
      }

      // Определить прямоугольник элемента на экране
      const hoverBoundingRect = ref.current.getBoundingClientRect();

      // Получить вертикальную середину
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Определить позицию мыши
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      // Получить пиксели до верха
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Перетаскивание вниз
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Перетаскивание вверх
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Выполнить перемещение
      onMove(dragIndex, hoverIndex);

      // Обновить индекс для следующих вызовов
      item.index = hoverIndex;
    }
  });

  // Подключить drag и drop к элементу
  drag(drop(ref));

  const imageUrl = getDoctorImageUrl(doctor);

  return (
    <tr
      ref={ref}
      className={`${isDragging ? 'opacity-50' : 'opacity-100'} transition-opacity duration-200`}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      {/* Drag handle */}
      <td className="w-8">
        <div className="flex items-center justify-center">
          <div className="cursor-grab hover:cursor-grabbing text-base-content/40 hover:text-base-content/60 transition-colors">
            <i className="fas fa-grip-vertical text-sm"></i>
          </div>
        </div>
      </td>

      {/* Фото */}
      <td>
        <div className="flex items-center justify-center">
          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-base-300">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={doctor.fullName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-base-200 flex items-center justify-center">
                <i className="fas fa-user text-base-content/40"></i>
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Имя */}
      <td>
        <div className="font-medium text-base-content">{doctor.fullName}</div>
      </td>

      {/* Специализация */}
      <td>
        <div className="text-base-content/70">{doctor.specialization}</div>
      </td>

      {/* Статус */}
      <td>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`badge ${
              doctor.isActive ? 'badge-success' : 'badge-error'
            } badge-sm`}
          >
            {doctor.isActive ? 'Активен' : 'Неактивен'}
          </span>
          {doctor.isAvailableOnline && (
            <span className="badge badge-sm" style={{ backgroundColor: '#52CABE', color: 'white' }}>
              Онлайн
            </span>
          )}
          {doctor.isAvailableAtHome && (
            <span className="badge badge-sm" style={{ backgroundColor: '#F97784', color: 'white' }}>
              На дом
            </span>
          )}
        </div>
      </td>

      {/* Действия */}
      <td>
        <div className="flex gap-2">
          <Link
            to={`/doctors/${doctor.id}/edit`}
            className="btn btn-primary btn-sm"
            title="Редактировать"
          >
            <i className="fas fa-edit"></i>
          </Link>
          <button
            onClick={() => onStatusChange(doctor)}
            className={`btn btn-sm ${
              doctor.isActive ? 'btn-warning' : 'btn-success'
            }`}
            title={doctor.isActive ? 'Деактивировать' : 'Активировать'}
          >
            <i
              className={`fas ${doctor.isActive ? 'fa-pause' : 'fa-play'}`}
            ></i>
          </button>
        </div>
      </td>
    </tr>
  );
};
